import { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

import { Send, CheckCircle2 } from 'lucide-react';

const faqs = [
  {
    question: 'How long does a typical construction project take?',
    answer: 'Project timelines vary based on scope and complexity. A residential home typically takes 6-12 months, while commercial projects can range from 12-24 months. During our initial consultation, we provide a detailed timeline specific to your project requirements.',
  },
  {
    question: 'Do you provide free estimates and consultations?',
    answer: 'Yes! We offer complimentary initial consultations and detailed project estimates. Our team will visit your site, discuss your requirements, and provide a comprehensive quote with no obligation.',
  },
  {
    question: 'What types of projects does AST Construction handle?',
    answer: 'We specialize in residential, commercial, and industrial construction. This includes new builds, renovations, remodeling, interior design, and project consultation. From luxury homes to office towers and manufacturing facilities, we have expertise across all sectors.',
  },
  {
    question: 'Are you licensed and insured?',
    answer: 'Absolutely. AST Construction is fully licensed, bonded, and insured. We hold all necessary certifications including ISO 9001:2015 for quality management.',
  },
  {
    question: 'How do you ensure quality control during construction?',
    answer: 'We implement a rigorous quality management system with regular inspections at every phase. Our dedicated quality assurance team conducts daily checks, and we use only premium materials from certified suppliers.',
  },
  {
    question: 'Can you work within a specific budget?',
    answer: 'Yes, we work closely with clients to design solutions that meet both their vision and budget. We provide transparent pricing, detailed cost breakdowns, and suggest value engineering options without compromising quality when needed.',
  },
];

const projectTypes = ['Residential', 'Commercial', 'Industrial', 'Renovation', 'Other'];

export const FAQSection = () => {
  const [formData, setFormData] = useState({
    name: '', email: '', projectType: '', phone: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: '', email: '', projectType: '', phone: '', message: '' });
  };

  return (
    <section
      className="py-6 md:py-10 bg-cover bg-center bg-fixed relative overflow-hidden"
      style={{ backgroundImage: `url("/assets/fequently asked section back home page.png")` }}
    >
      {/* Layered overlay */}
      <div className="absolute inset-0 bg-linear-to-b from-white/70 via-slate-50/60 to-white/70 backdrop-blur-[1px]" />

      {/* Decorative edge lines */}
      <div className="absolute top-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-amber-500/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-linear-to-r from-transparent via-amber-500/20 to-transparent" />

      <div className="container-custom relative z-10">
        {/* Heading */}
        <div className="text-center mb-5">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="w-8 h-px bg-amber-400/60" />
            <p className="text-amber-600 text-xs font-black uppercase tracking-[0.3em]">Get In Touch</p>
            <div className="w-8 h-px bg-amber-400/60" />
          </div>
          <h2 className="text-2xl md:text-4xl font-black text-slate-900 leading-tight">
            Frequently Asked <span className="text-amber-500">Questions</span>
          </h2>
          <p className="text-base text-slate-600 mt-2 font-medium">Find answers to common questions about our services</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 items-start">
          {/* ── Contact Form ── */}
          <div className="relative">
            {/* Card glow */}
            <div className="absolute -inset-1 bg-linear-to-br from-amber-500/20 to-transparent rounded-3xl blur-xl opacity-50" />
            <div className="relative bg-white backdrop-blur-xl rounded-3xl p-5 border border-slate-200 shadow-2xl overflow-hidden">
              {/* Top accent stripe */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-linear-to-r from-amber-400 via-orange-400 to-amber-400" />

              <h3 className="text-lg font-black text-slate-900 mb-1">Request a Free Consultation</h3>
              <p className="text-slate-500 text-sm mb-4 font-medium">Fill out the form and we'll get back to you.</p>

              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-linear-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/30">
                    <CheckCircle2 className="w-8 h-8 text-slate-900" />
                  </div>
                  <p className="text-slate-900 font-black text-lg">Message Sent!</p>
                  <p className="text-slate-500 mt-1 text-sm">We'll get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-amber-400 focus:bg-white transition-all"
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-amber-400 focus:bg-white transition-all"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <select
                      value={formData.projectType}
                      onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 text-sm focus:outline-none focus:border-amber-400 transition-all appearance-none"
                    >
                      <option value="" className="text-slate-800">Project Type</option>
                      {projectTypes.map(t => (
                        <option key={t} value={t} className="text-slate-800">{t}</option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      placeholder="Phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-amber-400 focus:bg-white transition-all"
                    />
                  </div>

                  <textarea
                    placeholder="MESSAGE"
                    required
                    rows={2}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 text-sm focus:outline-none focus:border-amber-400 focus:bg-white transition-all resize-none"
                  />

                  <button
                    type="submit"
                    className="relative w-full group bg-accent text-slate-900 font-black text-sm uppercase tracking-widest py-2.5 rounded-xl hover:bg-amber-400 transition-all shadow-lg shadow-amber-500/30 hover:shadow-amber-500/50 hover:-translate-y-0.5 flex items-center justify-center gap-2 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/20 translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
                    <Send className="w-4 h-4 relative z-10" />
                    <span className="relative z-10">Submit Request</span>
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* ── FAQ Accordion ── */}
          <div>
            <Accordion type="single" collapsible className="w-full space-y-2">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white rounded-xl border border-slate-200 px-4 shadow-sm hover:border-amber-400/60 transition-colors duration-200 overflow-hidden"
                >
                  <AccordionTrigger className="text-sm font-bold text-slate-800 hover:text-amber-600 text-left py-3 transition-colors data-[state=open]:text-amber-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-3 text-sm leading-relaxed border-t border-slate-100 pt-3 mt-1">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </div>
    </section>
  );
};
